import { Component, OnInit, AfterViewInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router, NavigationExtras } from '@angular/router';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { Location, LocationStrategy } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { Title } from '@angular/platform-browser';
import { OverlayContainer } from '@angular/cdk/overlay';
import * as _ from 'lodash';
import { Sort } from '@angular/material/sort';

// GSRS Imports
import { LoadingService } from '@gsrs-core/loading';
import { MainNotificationService } from '@gsrs-core/main-notification';
import { AppNotification, NotificationType } from '@gsrs-core/main-notification';
import { BulkSearchService } from '@gsrs-core/bulk-search/service/bulk-search.service';
import { ConfigService } from '@gsrs-core/config';
import { AuthService } from '@gsrs-core/auth/auth.service';
import { UtilsService } from '@gsrs-core/utils/utils.service';
import { ApplicationService } from '../service/application.service';
import { GeneralService } from '../../service/general.service';
import { environment } from '../../../../environments/environment';
import { GoogleAnalyticsService } from '../../../../app/core/google-analytics/google-analytics.service';
import { FacetParam, Facet, FacetsManagerService, FacetUpdateEvent } from '@gsrs-core/facets-manager';
import { DisplayFacet } from '@gsrs-core/facets-manager/display-facet';
import { NarrowSearchSuggestion } from '@gsrs-core/utils';
import { applicationSearchSortValues } from './application-search-sort-values';
import { ExportDialogComponent } from '@gsrs-core/substances-browse/export-dialog/export-dialog.component';
import { StructureImageModalComponent, StructureService } from '@gsrs-core/structure';
import { BulkSearchResultsSummaryComponent } from '@gsrs-core/bulk-search/bulk-search-results-summary/substances/bulk-search-results-summary.component';

import { Application } from '../model/application.model';

@Component({
    selector: 'app-applications-browse',
    templateUrl: './applications-browse.component.html',
    styleUrls: ['./applications-browse.component.scss'],
    standalone: false
})
export class ApplicationsBrowseComponent implements OnInit, AfterViewInit, OnDestroy {
  // @ViewChild('matSideNavInstance', { static: true }) matSideNav: MatSidenav;

  view = 'cards';
  public privateSearchTerm?: string;
  public applications: Array<Application>;
  order: string;
  public sortValues = applicationSearchSortValues;
  pageIndex: number;
  pageSize: number;
  jumpToValue: string;
  totalApplications: number;
  isLoading = true;
  isError = false;
  canUpdate: boolean = false;
  canExport: boolean = false;
  isLoggedIn = false;
  dataSource = [];
  hasBackdrop = false;
  appType: string;
  appNumber: string;
  clinicalTrialApplication: Array<any>;
  exportUrl: string;
  private isComponentInit = false;
  privateExport = false;
  disableExport = false;
  private overlayContainer: HTMLElement;
  etag = '';
  environment: any;
  narrowSearchSuggestions?: { [matchType: string]: Array<NarrowSearchSuggestion> } = {};
  matchTypes?: Array<string> = [];
  narrowSearchSuggestionsCount = 0;
  previousState: Array<string> = [];
  private searchTermHash: number;
  isSearchEditable = false;
  searchValue: string;
  lastPage: number;
  invalidPage = false;

  // Cross Entity Search
  thisEntity: string = "applications";
  subEntity = null;
  subEntitySearchTerm = null;
  showCrossEntitySearch = false;
  searchOnIdentifiers = false;
  bulkSearchPanelOpen = false;
  isSearchFinished = false;
  iterations: number = 0;
  bulkSearchStatusKey: string;
  searchStatusUrl: string;
  searchEntity: string;
  subEntitySearchHash: string;
  editSubEntitySearchHash: any;
  bulkSearchQueryId?: number;
  idLists: Array<string> = [];
  secondIdLists: Array<string> = [];
  subEntityDisplayFacets: Array<DisplayFacet> = [];

  ascDescDir = 'desc';
  displayedColumns: string[] = [
    'appType',
    'appNumber',
    'center',
    'provenance',
    'applicationStatus',
    'productName',
    'sponsorName',
    'ingredientName'
  ];

  // needed for facets
  private privateFacetParams: FacetParam;
  rawFacets: Array<Facet>;
  private isFacetsParamsInit = false;
  public displayFacets: Array<DisplayFacet> = [];
  private subscriptions: Array<Subscription> = [];

  constructor(
    public bulkSearchService: BulkSearchService,
    public applicationService: ApplicationService,
    public generalService: GeneralService,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private locationStrategy: LocationStrategy,
    private router: Router,
    private sanitizer: DomSanitizer,
    public gaService: GoogleAnalyticsService,
    public configService: ConfigService,
    private loadingService: LoadingService,
    private notificationService: MainNotificationService,
    private authService: AuthService,
    private overlayContainerService: OverlayContainer,
    private facetManagerService: FacetsManagerService,
    private utilsService: UtilsService,
    private dialog: MatDialog,
    private titleService: Title
  ) { }

  @HostListener('window:popstate', ['$event'])
  onPopState(event) {
    setTimeout(() => {
      if (this.router.url === this.previousState[0]) {
        this.ngOnInit();
      }

    }, 50);
  }

  async ngOnInit() {
    this.facetManagerService.registerGetFacetsHandler(this.applicationService.getApplicationFacets);
    this.gaService.sendPageView('Browse Applications');

    this.titleService.setTitle(`Application Browser`);

    // get config value for 'crossEntitySearch'. if it is true show dropdown 'Show Facet For'
    this.showCrossEntitySearch = this.configService.configData.showCrossEntitySearchDropdown || false;

    this.pageSize = 10;
    this.pageIndex = 0;

    const navigationExtras: NavigationExtras = {
      queryParams: {}
    };

    this.privateSearchTerm = this.activatedRoute.snapshot.queryParams['search'] || '';

    if (this.privateSearchTerm) {
      this.searchTermHash = this.utilsService.hashCode(this.privateSearchTerm);
      this.isSearchEditable = localStorage.getItem(this.searchTermHash.toString()) != null;
    }

    this.order = this.activatedRoute.snapshot.queryParams['order'] || '$root_lastModifiedDate';
    this.pageSize = parseInt(this.activatedRoute.snapshot.queryParams['pageSize'], null) || 10;
    this.pageIndex = parseInt(this.activatedRoute.snapshot.queryParams['pageIndex'], null) || 0;

    this.bulkSearchQueryId = this.activatedRoute.snapshot.queryParams['bulkQID'] || '';
    this.searchEntity = this.activatedRoute.snapshot.queryParams['searchEntity'] || '';

    // For Cross Entity/Sub Entity Search
    this.subEntitySearchHash = this.activatedRoute.snapshot.queryParams['subentity-hash'];

    this.overlayContainer = this.overlayContainerService.getContainerElement();

    // Get Admin privilege
    const authSubscription = this.authService.getAuth().subscribe(auth => {
      if (auth) {
        this.isLoggedIn = true;
      }
    });
    this.subscriptions.push(authSubscription);

    const paramsSubscription = this.activatedRoute.queryParamMap.subscribe(params => {
      this.searchValue = params.get('search');
    });

    this.subscriptions.push(paramsSubscription);

    if (this.subEntitySearchHash) {
      // if cross entity search is performed, show the facets selected for Cross Entity/Sub Entity Search
      this.subEntityfacetDisplay();
    }

    this.isComponentInit = true;
    this.loadComponent();
    this.canUpdate = await this.authService.hasSpecificPrivilege('Edit');
    this.canExport = await this.authService.hasSpecificPrivilege('Export Data');
  }

  ngAfterViewInit() {
  }

  ngOnDestroy() {
    this.subscriptions.forEach(subscription => {
      if (subscription) {
        subscription.unsubscribe();
      }
    });
    this.facetManagerService.unregisterFacetSearchHandler();
  }

  private loadComponent(): void {
    if (this.isFacetsParamsInit && this.isComponentInit) {
      this.searchApplications();
    }
  }

  searchApplications() {
    this.loadingService.setLoading(true);

    const skip = this.pageIndex * this.pageSize;
    let fdim = 10;
    this.iterations = 0;

    const subscription = this.applicationService.getApplications(
      this.order,
      skip,
      this.pageSize,
      fdim,
      this.privateSearchTerm,
      this.privateFacetParams,
      this.bulkSearchQueryId
    ).subscribe(pagingResponse => {
      this.isError = false;
      // Get Bulk Search Status Key and url. The hostname for bulk search result can be different for non-substance entity
      this.bulkSearchStatusKey = pagingResponse.statusKey;
      this.searchStatusUrl = pagingResponse.searchStatusUrl;

      //this.applications = pagingResponse.content;

      // didn't work unless I did it like this instead of
      // below export statement
      //this.dataSource = this.applications;

      // if Bulk Search is not finished
      if (pagingResponse.finished) {
        this.isSearchFinished = true;
      }

      // if it is Bulk Search
      if (this.bulkSearchQueryId && this.iterations === 0) {
        this.iterations++;

        this.applications = pagingResponse.content;
        this.dataSource = this.applications;
      } else {
        this.applications = pagingResponse.content;
        this.dataSource = this.applications;
      }

      this.totalApplications = pagingResponse.total;
      this.etag = pagingResponse.etag;

      if (pagingResponse.total % this.pageSize === 0) {
        this.lastPage = (pagingResponse.total / this.pageSize);
      } else {
        this.lastPage = Math.floor(pagingResponse.total / this.pageSize + 1);
      }

      if (pagingResponse.facets && pagingResponse.facets.length > 0) {
        this.rawFacets = pagingResponse.facets;
      }

      // Narrow Suggest Search Begin
      this.narrowSearchSuggestions = {};
      this.matchTypes = [];
      this.narrowSearchSuggestionsCount = 0;

      if (pagingResponse.narrowSearchSuggestions && pagingResponse.narrowSearchSuggestions.length) {
        pagingResponse.narrowSearchSuggestions.forEach(suggestion => {
          if (this.narrowSearchSuggestions[suggestion.matchType] == null) {
            this.narrowSearchSuggestions[suggestion.matchType] = [];
            if (suggestion.matchType === 'WORD') {
              this.matchTypes.unshift(suggestion.matchType);
            } else {
              this.matchTypes.push(suggestion.matchType);
            }
          }
          this.narrowSearchSuggestions[suggestion.matchType].push(suggestion);
          this.narrowSearchSuggestionsCount++;
        });
      }

      this.matchTypes.sort();
      // Narrow Suggest Search End

      this.getSubstanceBySubstanceKey();

      // Get Application Clinical Trial Record
      this.getClinicalTrialApplication();

      // Stop the spinner
      if (this.bulkSearchQueryId && this.iterations === 1) {
        this.isLoading = false;
        this.loadingService.setLoading(this.isLoading);
      }

    }, error => {
      console.log('Applications fetch failed:', error);
      const notification: AppNotification = {
        message: 'There was an error trying to retrieve Applications. Please refresh and try again.',
        type: NotificationType.error,
        milisecondsToShow: 6000
      };
      this.isError = true;
      this.isLoading = false;
      this.loadingService.setLoading(this.isLoading);
      this.notificationService.setNotification(notification);
    }, () => {
      subscription.unsubscribe();
      this.isLoading = false;
      this.loadingService.setLoading(this.isLoading);
    });
  }

  setSearchTermValue() {
    this.pageSize = 10;
    this.pageIndex = 0;
    this.searchApplications();
  }

  clearFilters(): void {
    // for facets
    this.displayFacets.forEach(displayFacet => {
      displayFacet.removeFacet(displayFacet.type, displayFacet.bool, displayFacet.val);
    });

    if (this.bulkSearchQueryId) {
      this.clearBulkSearch();
    } else {
      this.clearSearch();
    }

    this.facetManagerService.clearSelections();
  }

  clearSearch(): void {
    const eventLabel = environment.isAnalyticsPrivate ? 'search term' : this.privateSearchTerm;
    this.gaService.sendEvent('applicationFiltering', 'icon-button:clear-search', eventLabel);

    this.pageIndex = 0;
    this.pageSize = 10;
    this.privateSearchTerm = '';
    this.searchValue = '';

    this.populateUrlQueryParameters();

    this.searchApplications();
  }

  clearBulkSearch(): void {
    this.order = '$root_lastModifiedDate';
    this.pageSize = 10;
    this.pageIndex = 0;
    this.privateSearchTerm = '';
    this.searchValue = '';

    this.bulkSearchQueryId = null;
    this.bulkSearchStatusKey = '';
    this.searchEntity = '';
    this.subEntitySearchHash = '';
    this.subEntityDisplayFacets = [];

    this.populateUrlQueryParameters();

    // Reset bulk search and do regular search
    this.searchApplications();
  }

  populateUrlQueryParameters(): void {
    const navigationExtras: NavigationExtras = {
      queryParams: {}
    };

    //navigationExtras.queryParams['searchTerm'] = this.privateSearchTerm;
    navigationExtras.queryParams['search'] = this.privateSearchTerm;
    navigationExtras.queryParams['pageSize'] = this.pageSize;
    navigationExtras.queryParams['pageIndex'] = this.pageIndex;
    navigationExtras.queryParams['skip'] = this.pageIndex * this.pageSize;

    navigationExtras.queryParams['bulkQID'] = this.bulkSearchQueryId;
    navigationExtras.queryParams['searchEntity'] = this.searchEntity;
    navigationExtras.queryParams['subentity-hash'] = this.subEntitySearchHash;

    this.previousState.push(this.router.url);
    const urlTree = this.router.createUrlTree([], {
      queryParams: navigationExtras.queryParams,
      queryParamsHandling: 'merge',
      preserveFragment: true
    });
    this.location.go(urlTree.toString());
  }

  get searchTerm(): string {
    return this.privateSearchTerm;
  }

  // get facetParams(): FacetParam | { showAllMatchOption?: boolean } {
  //   return this.privateFacetParams;
  // }

  sortData(sort: Sort) {
    if (sort.active) {
      const orderIndex = this.displayedColumns.indexOf(sort.active).toString();
      this.ascDescDir = sort.direction;
      this.sortValues.forEach(sortValue => {
        if (sortValue.displayedColumns && sortValue.direction) {
          if (this.displayedColumns[orderIndex] === sortValue.displayedColumns && this.ascDescDir === sortValue.direction) {
            this.order = sortValue.value;
          }
        }
      });
      // Search Applications
      this.searchApplications();
    }
    return;
  }

  openSideNav() {
    this.gaService.sendEvent('substancesFiltering', 'button:sidenav', 'open');
    // this.matSideNav.open();
  }

  updateView(event): void {
    // this.gaService.sendEvent('adverseeventptsContent', 'button:view-update', event.value);
    this.view = event.value;
  }

  changePage(pageEvent: PageEvent) {

    let eventAction;
    let eventValue;

    if (this.pageSize !== pageEvent.pageSize) {
      eventAction = 'select:page-size';
      eventValue = pageEvent.pageSize;
    } else if (this.pageIndex !== pageEvent.pageIndex) {
      eventAction = 'icon-button:page-number';
      eventValue = pageEvent.pageIndex + 1;
    }

    this.gaService.sendEvent('applicationsContent', eventAction, 'pager', eventValue);

    this.pageSize = pageEvent.pageSize;
    this.pageIndex = pageEvent.pageIndex;
    this.populateUrlQueryParameters();
    this.searchApplications();
  }

  customPage(event: any): void {
    if (this.validatePageInput(event)) {
      this.invalidPage = false;
      const newpage = Number(event.target.value) - 1;
      this.pageIndex = newpage;
      this.gaService.sendEvent('applicationsContent', 'select:page-number', 'pager', newpage);
      this.populateUrlQueryParameters();
      this.searchApplications();
    }
  }

  validatePageInput(event: any): boolean {
    if (event && event.target) {
      const newpage = Number(event.target.value);
      if (!isNaN(Number(newpage))) {
        if ((Number.isInteger(newpage)) && (newpage <= this.lastPage) && (newpage > 0)) {
          return true;
        }
      }
    }
    return false;
  }

  // for facets
  facetsParamsUpdated(facetsUpdateEvent: FacetUpdateEvent): void {
    this.pageIndex = 0;
    this.privateFacetParams = facetsUpdateEvent.facetParam;
    this.displayFacets = facetsUpdateEvent.displayFacets;
    if (!this.isFacetsParamsInit) {
      // Defer to avoid ExpressionChangedAfterItHasBeenCheckedError
      Promise.resolve().then(() => {
        this.isFacetsParamsInit = true;
        this.loadComponent();
      });
    } else {
      this.searchApplications();
    }
  }

  // for facets
  facetsLoaded(numFacetsLoaded: number) {
  }

  editAdvancedSearch(): void {
    const eventLabel = environment.isAnalyticsPrivate ? 'Browse Application search term' :
      `${this.privateSearchTerm}`;
    this.gaService.sendEvent('Application Filtering', 'icon-button:edit-advanced-search', eventLabel);

    const navigationExtras: NavigationExtras = {
      queryParams: {
        'g-search-hash': this.searchTermHash.toString()
      }
    };

    this.router.navigate(['/advanced-search'], navigationExtras);
  }

  getSubstanceBySubstanceKey(): void {
    // let bdnumName: any;
    // let relationship: any;
    // let substanceId: string;

    this.applications.forEach((element, index) => {

      if (element.applicationProductList && element.applicationProductList.length > 0) {
        element.applicationProductList.forEach((elementProd, indexProd) => {

          // Sort Product Name by create date descending
          elementProd.applicationProductNameList.sort((a, b) => {
            return <any>new Date(b.creationDate) - <any>new Date(a.creationDate);
          });

          elementProd.applicationIngredientList.forEach((elementIngred, indexIngred) => {
            if (elementIngred.substanceKey != null) {

              const substanceSubscription = this.generalService.getSubstanceByAnyId(elementIngred.substanceKey).subscribe(response => {
                if (response) {
                  // Get Substance Details, uuid, approval_id, substance name
                  if (elementIngred.substanceKey) {
                    this.generalService.getSubstanceByAnyId(elementIngred.substanceKey).subscribe(responseInactive => {
                      if (responseInactive) {
                        elementIngred._substanceUuid = responseInactive.uuid;
                        elementIngred._ingredientName = responseInactive._name;
                      }
                    });
                  }

                  // Get Active Moiety - Relationship
                  /*
                  this.applicationService.getSubstanceRelationship(substanceId).subscribe(responseRel => {
                    relationship = responseRel;
                    relationship.forEach((elementRel, indexRel) => {
                      if (elementRel.relationshipName != null) {
                        elementIngred.activeMoietyName = elementRel.relationshipName;
                        elementIngred.activeMoietyUnii = elementRel.relationshipUnii;
                      }
                    });
                  });
                  */
                }
              });
              this.subscriptions.push(substanceSubscription);
            }
          }); // Ingredient forEach

        }); // Product forEach

      }
    }); // Application forEach

  }

  restricSearh(searchTerm: string): void {
    this.privateSearchTerm = searchTerm;
    this.searchTermHash = this.utilsService.hashCode(this.privateSearchTerm);
    this.isSearchEditable = localStorage.getItem(this.searchTermHash.toString()) != null;
    this.populateUrlQueryParameters();
    this.searchApplications();
    // this.substanceTextSearchService.setSearchValue('main-substance-search', this.privateSearchTerm);
  }

  export() {
    if (this.etag) {
      const extension = 'xlsx';
      const url = this.getApiExportUrl(this.etag, extension);
      //  if (this.authService.getUser() !== '') {
      const dialogReference = this.dialog.open(ExportDialogComponent, {
        //  height: '215x',
        width: '700px',
        data: { 'extension': extension, 'type': 'BrowseApplications', 'entity': 'applications', 'hideOptionButtons': true }
      });
      // this.overlayContainer.style.zIndex = '1002';
      dialogReference.afterClosed().subscribe(response => {
        // this.overlayContainer.style.zIndex = null;
        const name = response.name;
        const id = response.id;
        if (name && name !== '') {
          this.loadingService.setLoading(true);
          const fullname = name + '.' + extension;
          this.authService.startUserDownload(url, this.privateExport, fullname, id).subscribe(response => {
            this.loadingService.setLoading(false);
            const navigationExtras: NavigationExtras = {
              queryParams: {
                totalSub: this.totalApplications
              }
            };
            const params = { 'total': this.totalApplications };
            this.router.navigate(['/user-downloads/', response.id]);
          }, error => { this.loadingService.setLoading(false); });
        }
      });
      //  }
    }
  }

  getApiExportUrl(etag: string, extension: string): string {
    return this.applicationService.getApiExportUrl(etag, extension);
  }

  get updateApplicationUrl(): string {
    return this.applicationService.getUpdateApplicationUrl();
  }

  getClinicalTrialApplication() {
    this.applications.forEach((app, index) => {
      // Get Clinical Trial Application
      const clinicalSubscription = this.applicationService.getClinicalTrialApplication(app.id).subscribe(response => {
        app._clinicalTrialList = [];
        app._clinicalTrialList = response;
      })
      this.subscriptions.push(clinicalSubscription);
    });
  }

  openImageModal($event, subUuid: string): void {
    // const eventLabel = environment.isAnalyticsPrivate ? 'substance' : substance._name;

    //  this.gaService.sendEvent('substancesContent', 'link:structure-zoom', eventLabel);

    let data: any;

    // if (substance.substanceClass === 'chemical') {
    data = {
      structure: subUuid,
      //   smiles: substance.structure.smiles,
      uuid: subUuid,
      //    names: substance.names
    };
    // }

    const dialogRef = this.dialog.open(StructureImageModalComponent, {
      height: '90%',
      width: '650px',
      panelClass: 'structure-image-panel',
      data: data
    });

    this.overlayContainer.style.zIndex = '1002';

    const subscription = dialogRef.afterClosed().subscribe(() => {
      this.overlayContainer.style.zIndex = null;
      subscription.unsubscribe();
    }, () => {
      this.overlayContainer.style.zIndex = null;
      subscription.unsubscribe();
    });
  }

  processSubstanceSearch(searchValue: string) {
    this.privateSearchTerm = searchValue;
    this.setSearchTermValue();
  }

  increaseOverlayZindex(): void {
    this.overlayContainer.style.zIndex = '1002';
  }

  decreaseOverlayZindex(): void {
    this.overlayContainer.style.zIndex = null;
  }

  createQueryTextWithIds(idListForSearch: Array<string>, entity: string, rootId: boolean = false): string {
    let queryText = '';

    idListForSearch.forEach((id, index) => {
      if (id) {
        if (index > 0) {
          queryText = queryText + '\n';
        }
        if (entity && entity === 'substances') {
          queryText = queryText + 'root_uuid:"' + id + '"';
        } else {
          if (rootId) {
            queryText = queryText + 'root_id:"' + id + '"';
          } else {
            queryText = queryText + 'entity_link_substances:"' + id + '"';
          }
        }
      }
    });

    return queryText;
  }

  getBulkQuery(substanceIdLists: Array<string>, entity: string) {
    let queryText = this.createQueryTextWithIds(substanceIdLists, entity);

    if (queryText) {
      this.bulkSearchService.postOrPutBulkQuery(entity, queryText).subscribe(result => {
        if (result) {
          if (result.id) {
            this.forwardToSubstance(result.id);
          }
        }
      });
    }
  }

  showAllSubstances() {
    this.getSearchIdsOnly(true, "all substances");
  }

  forwardToSubstance(bulkQID: number) {
    // Store current url in cookies

    const searchItemHash = this.utilsService.hashCode();

    // Store parameters in local storage
    // localStorage.setItem(searchItemHash.toString(), JSON.stringify(item));

    const navigationExtras: NavigationExtras = {
      queryParams: {}
    };

    if (bulkQID > 0) {
      navigationExtras.queryParams['bulkQID'] = bulkQID;
    }

    this.router.navigate(['/browse-substance'], navigationExtras);
  }

  // Need this for Cross Entity Search. Return all the IDs only for the current search
  getSearchIdsOnly(doPerformSearch: boolean = true, searchType?: string) {

    if (doPerformSearch) {
      let idListsTemp: Array<string> = [];

      let order = null;
      let top = 10;
      let skip = 0;
      let fdim = 1000000;
      let bulkQID = null;
      let searchOnIdentifiers = false;
      let view = 'key';
      let viewfield = null;
      let facetlabel = 'Substance UUID';

      const subscription = this.applicationService.getApplicationFacetWithSearchCriteria(
        facetlabel,
        this.privateFacetParams,
        this.privateSearchTerm,
        fdim
      ).subscribe(response => {
        if (response) {
          if (response.content && response.content.length > 0) {
            // *** For Cross Entity Search get lists of Substance UUID ***
            response.content.forEach((facet, index) => {
              if (facet) {
                if (facet.label) {
                  idListsTemp.push(facet.label);
                }
              }
              // Copy after the last record
              if (response.content.length == index + 1) {

                if (searchType && searchType === 'all substances') {
                  this.getBulkQuery(idListsTemp, 'substances',);
                } else {
                  // Get Search Product Ids for Cross Entity Search
                  this.getSearchApplicationIds(idListsTemp);
                }

                // For Cross Entity Search, copy idListTemp to idList after the loop so that change detection happens only once
                // this.idLists = idListsTemp;
              }
            }); // forEach    

          } // if content 
          else {
            this.idLists = [];
          }
        } // response
        else {
          this.idLists = [];
        }

      }, error => {
        console.log('Error during search application');
      }, () => {
        this.subscriptions.push(subscription);
      }
      ); // response

      /*
      const subscription = this.applicationService.getApplications(
        order,
        skip,
        top,
        fdim,
        this.privateSearchTerm,
        this.privateFacetParams,
        bulkQID,
        view,
        viewfield,
        facetlabel
      )
        .subscribe(pagingResponse => {
          // Get Facets from paging response
          if (pagingResponse.facets && pagingResponse.facets.length > 0) {
            const facets = pagingResponse.facets;

            // *** For Cross Entity Search get lists of Substance UUID ***
            let facetSubUuid = facets.find(facet => facet.name === "Substance UUID");

            if (facetSubUuid) {
              facetSubUuid.values.forEach((value, index) => {
                if (value) {
                  if (value.label) {
                    idListsTemp.push(value.label);
                  }
                }

                // Copy after the last record
                if (facetSubUuid.values.length == index + 1) {
                  // For Cross Entity Search, copy idListTemp to idList after the loop so that change detection happens only once
                  this.idLists = idListsTemp;
                }
              }); // forEach

            } else {
              this.idLists = [];
            }
          } // pagingResponse.facets

        }, error => {
          console.log('Error during search substance');
        }, () => {
          this.subscriptions.push(subscription);
        }
        ); // pagingResponse
      */

    } // if doPerformSearch == true
  }

  getSearchApplicationIds(substanceUuids?: any) {
    let idListsTemp: Array<string> = [];

    let order = null;
    let top = 1000000;
    let skip = 0;
    let fdim = 10;
    let bulkQID = null;
    let searchOnIdentifiers = false;
    let view = 'key';
    // let viewfield = 'facet';
    //let facetlabel = 'Substance UUID';
    let facetlabel = null;
    let viewfield = 'id';
    let simpleSearchOnly: boolean = true;

    // Get Product records that have Ingredients
    this.privateFacetParams['Has Ingredients'] = { 'params': { 'Has Ingredients': true }, 'isAllMatch': false };

    const subscription = this.applicationService.getApplications(
      order,
      skip,
      top,
      fdim,
      this.privateSearchTerm,
      this.privateFacetParams,
      bulkQID,
      view,
      viewfield,
      facetlabel
    ).subscribe(pagingResponse => {
      if (pagingResponse.content) {
        let results: any = pagingResponse.content;

        // Loop through each product record and get product id
        if (results.length > 0) {
          results.forEach((applicationId, index) => {
            // for Cross Entity Search, add Application Ids in the temporary list
            idListsTemp.push(applicationId);

            // Copy after the last record
            if (results.length == index + 1) {

              this.idLists = substanceUuids;
              this.secondIdLists = idListsTemp;
            }
          }); // forEach
        } else {
          //this.idLists = [];
        }
      }  // pagingResponse   
    }, error => {
      console.log('Error during search application');
    }, () => {
      this.subscriptions.push(subscription);
    }

    ); // pagingResponse
  }

  subEntityfacetDisplay() {
    // if (hashcode is found on the url)
    if (this.subEntitySearchHash) {
      // Get Sub-entity facet values from local Storage to display on Browse Substance page
      const searchParams = localStorage.getItem(this.subEntitySearchHash);

      if (searchParams) {
        const searchParamItems = JSON.parse(searchParams);
        if (searchParamItems) {
          this.subEntity = searchParamItems['subEntityDisplay'];
          this.subEntityDisplayFacets = searchParamItems['subEntityDisplayFacets'];
        }
      }
    }
  }

  editSubEntitySearch(): void {
    if (this.subEntitySearchHash) {
      /*
      const searchParam: Array<String> = [];

      const searchParams = localStorage.getItem(this.subEntitySearchHash);

      if (searchParams) {
        const searchParamItems = JSON.parse(searchParams);
        if (searchParamItems) {
          this.subEntity = searchParamItems['subEntity'];
          this.subEntityDisplayFacets = searchParamItems['subEntityDisplayFacets'];
        }
      } 
      */
      let randomInteger = Math.floor(Math.random() * 100000000);
      // Using random number to create different value, so it will trigger change Detection for Input
      this.editSubEntitySearchHash = this.subEntitySearchHash + '_' + randomInteger;
    }
  }

}
